// Foundations
import {
  AfterViewInit,
  Component,
  HostBinding,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatTab, MatTabGroup, MatTabHeader } from '@angular/material/tabs';
import { Title } from '@angular/platform-browser';

// rxjs
import { filter, map, take } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';

// Services
import { AnalyticsService } from '@app/services/analytics.service';
import { PetService } from '@app/services/pet.service';
import { DialogService } from '@app/services/dialog.service';
import {
  GetPetsWithStatusGQL,
  PetWithStatus,
} from '@app/services/graphql.service';
import { AuthService } from '@app/auth/auth.service';
import { MyProfileService } from '@app/services/my-profile.service';

// Model
import { CanDeactivateComponent } from '@app/models/can-deactivate-component.model';
import { ParsedPet } from '@app/models/pet/pet.model';
import { PetStatus, PetStatusType } from '@app/models/pet/pet-status.model';

// Components
import { CamperDetailsComponent } from '@app/components/dog-profile/camper-details/camper-details.component';
import { HealthRecordsComponent } from '@app/components/dog-profile/health-records/health-records.component';
import { DietMedsComponent } from '@app/components/dog-profile/diet-meds/diet-meds.component';

@Component({
  selector: 'app-dog-profile-page',
  templateUrl: './dog-profile-page.component.html',
  styleUrls: ['./dog-profile-page.component.scss'],
})
export class DogProfilePageComponent
  implements OnInit, AfterViewInit, CanDeactivateComponent
{
  @HostBinding('class') class = 'page-config';
  @ViewChild('cdPage', { static: false }) cdPage: CamperDetailsComponent;
  @ViewChild('hrPage', { static: false }) hrPage: HealthRecordsComponent;
  @ViewChild('dmPage', { static: false }) dmPage: DietMedsComponent;
  @ViewChild('tabs', { static: false }) tabs: MatTabGroup;

  // TODO: Dog Profile routing
  // 1. "Add a pet" -> '/camper-profile/add'
  // 2. "View/Edit a pet" -> '/camper-profile/:id'

  currIndex = 0; // determines the index of tab
  dogRemoved = false; // set to true to view dog removed survey

  petId: number;
  petName: string;
  pet: ParsedPet;

  petTileList$: Observable<PetWithStatus[]>;
  petTileList: PetWithStatus[];

  constructor(
    private route: ActivatedRoute,
    private _analytics: AnalyticsService,
    private dialogService: DialogService,
    private titleService: Title,
    private petService: PetService
  ) {
    this.titleService.setTitle('Client Portal - Camper Profile');
    this.petId = +this.route.snapshot.paramMap.get('id');
  }

  canDeactivate() {
    if (
      (this.cdPage && this.cdPage.form && !this.cdPage.form.pristine) ||
      (this.hrPage && this.hrPage.form && !this.hrPage.form.pristine) ||
      (this.dmPage && this.dmPage.form && !this.dmPage.form.pristine)
    ) {
      return this.dialogService.confirmDiscard();
    }
    return true;
  }

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._analytics.logScreen('dogProfilePage');

    this._prepData();
  }

  ngAfterViewInit(): void {
    this._prepForms();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  handleDogRemoved(): void {
    this.dogRemoved = true;
  }

  navigateToTabIndex(tabIndex: number) {
    this.currIndex = tabIndex;
  }

  onSelectedIndexChanged(args) {
    this.currIndex = args.newIndex;
  }

  handlePetNameChange(petName: string) {
    this.petName = petName;
  }

  get localProfileIcon() {
    return this.petService.getLocalProfileIcon(this.petId.toString());
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private interceptTabChange(
    tab: MatTab,
    tabHeader: MatTabHeader,
    idx: number
  ) {
    if (this.currIndex === idx) {
      return;
    }
    if (
      (this.cdPage?.form && !this.cdPage.form.pristine) ||
      (this.hrPage?.form && !this.hrPage.form.pristine) ||
      (this.dmPage?.form && !this.dmPage.form.pristine)
    ) {
      this.dialogService.confirmDiscard().subscribe((isDiscard) => {
        if (isDiscard) {
          this.cdPage.resetFormToDefault();
          this.hrPage.resetFormToDefault();
          this.dmPage.resetFormToDefault();
          return MatTabGroup.prototype._handleClick.apply(this.tabs, [
            tab,
            tabHeader,
            idx,
          ]);
        }
      });
    } else {
      MatTabGroup.prototype._handleClick.apply(this.tabs, [
        tab,
        tabHeader,
        idx,
      ]);
    }
  }

  private _prepData() {
    // Get the pet info
    this.pet = this.route.snapshot.data.petInfo as ParsedPet;
    if (this.pet?.active) {
      this.petName = this.pet.firstName
        ? this.pet.firstName + ' ' + this.pet.lastName
        : this.pet.nickName;
    }
  }

  private _prepForms() {
    this.tabs._handleClick = this.interceptTabChange.bind(this);

    //
    if (this.pet?.active) {
      // Add a `delay` to avoid potential rendering issues.
      setTimeout(() => {
        this.cdPage.populateForms(this.pet);
        this.hrPage.populateForms(this.pet);
        this.dmPage.populateForms(this.pet);
      }, 200);
    }
  }
}
